import { jsPDF } from 'jspdf';
import { getPage, getActs, TOTAL_PAGES } from '../data/tome';
import { PageData } from '../types/tome';

export interface PdfExportOptions {
  startPage?: number;
  endPage?: number;
  includeCover?: boolean;
  includeToc?: boolean;
  darkTheme?: boolean;
  onProgress?: (progressPercent: number, statusText: string) => void;
}

/**
 * Builds a publication-grade, beautifully formatted PDF volume of "The Caesura Engine".
 */
export async function buildBookPDF(options: PdfExportOptions = {}): Promise<jsPDF> {
  const {
    startPage = 1,
    endPage = Math.min(100, TOTAL_PAGES), // Default to first 100 pages for rapid browser export, configurable to 1000
    includeCover = true,
    includeToc = true,
    onProgress,
  } = options;

  onProgress?.(5, 'Initializing PDF Book Typesetter...');

  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'pt',
    format: 'a4', // 595.28 x 841.89 pt
  });

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 54; // 0.75 inch
  const contentWidth = pageWidth - margin * 2;

  // -------------------------------------------------------------
  // 1. Cover Page
  // -------------------------------------------------------------
  if (includeCover) {
    onProgress?.(10, 'Typesetting Cover & Frontispiece...');

    // Void black background
    doc.setFillColor(5, 7, 10);
    doc.rect(0, 0, pageWidth, pageHeight, 'F');

    // Geometric border
    doc.setDrawColor(0, 243, 255);
    doc.setLineWidth(1.5);
    doc.rect(margin - 10, margin - 10, contentWidth + 20, pageHeight - (margin - 10) * 2);

    doc.setDrawColor(157, 78, 221);
    doc.setLineWidth(0.75);
    doc.rect(margin - 6, margin - 6, contentWidth + 12, pageHeight - (margin - 6) * 2);

    // Title
    doc.setTextColor(0, 243, 255);
    doc.setFont('times', 'bold');
    doc.setFontSize(32);
    doc.text('THE CAESURA ENGINE', pageWidth / 2, 220, { align: 'center' });

    // Subtitle
    doc.setTextColor(216, 226, 236);
    doc.setFont('times', 'italic');
    doc.setFontSize(14);
    doc.text('A Literary Chronicle of the Discrete Spacetime Interval', pageWidth / 2, 255, {
      align: 'center',
    });
    doc.text('and the Unrendered Void', pageWidth / 2, 275, { align: 'center' });

    // Thin cyan divider
    doc.setDrawColor(0, 243, 255);
    doc.setLineWidth(1);
    doc.line(pageWidth / 2 - 80, 305, pageWidth / 2 + 80, 305);

    // Vessel & Author Callout
    doc.setTextColor(157, 78, 221);
    doc.setFont('courier', 'bold');
    doc.setFontSize(11);
    doc.text('USSC GERYON // HULL 0x992B-THETA', pageWidth / 2, 340, { align: 'center' });

    doc.setTextColor(216, 226, 236);
    doc.setFont('times', 'normal');
    doc.setFontSize(13);
    doc.text('Senior Jump Diagnostics Engineer Marcus Ward', pageWidth / 2, 480, {
      align: 'center',
    });

    doc.setFontSize(10);
    doc.setTextColor(107, 124, 150);
    doc.text('Thorne-Vance Deep Transit Combine // Perseus Registry', pageWidth / 2, 502, {
      align: 'center',
    });

    doc.setFont('courier', 'normal');
    doc.setFontSize(9);
    doc.text('1,000-PAGE COLLECTOR\'S EDITION // CERTIFIED LEDGER RECORD', pageWidth / 2, 740, {
      align: 'center',
    });
  }

  // -------------------------------------------------------------
  // 2. Table of Contents
  // -------------------------------------------------------------
  if (includeToc) {
    onProgress?.(20, 'Building Table of Contents...');
    doc.addPage();

    doc.setFillColor(8, 12, 18);
    doc.rect(0, 0, pageWidth, pageHeight, 'F');

    doc.setTextColor(0, 243, 255);
    doc.setFont('times', 'bold');
    doc.setFontSize(22);
    doc.text('TABLE OF ACTS & DISCRETE INTERVALS', margin, 90);

    doc.setDrawColor(0, 243, 255);
    doc.setLineWidth(1);
    doc.line(margin, 105, pageWidth - margin, 105);

    const acts = getActs();
    let y = 140;

    for (const act of acts) {
      if (y > pageHeight - 80) {
        doc.addPage();
        doc.setFillColor(8, 12, 18);
        doc.rect(0, 0, pageWidth, pageHeight, 'F');
        y = 90;
      }

      doc.setTextColor(255, 183, 0);
      doc.setFont('courier', 'bold');
      doc.setFontSize(11);
      doc.text(`ACT ${act.actNumber.toString().padStart(2, '0')}`, margin, y);

      doc.setTextColor(216, 226, 236);
      doc.setFont('times', 'bold');
      doc.setFontSize(12);
      doc.text(act.title, margin + 60, y);

      doc.setTextColor(0, 243, 255);
      doc.setFont('courier', 'normal');
      doc.setFontSize(10);
      doc.text(`pp. ${act.startPage} – ${act.endPage}`, pageWidth - margin, y, { align: 'right' });

      y += 18;
      doc.setTextColor(107, 124, 150);
      doc.setFont('times', 'italic');
      doc.setFontSize(9.5);
      const descLines = doc.splitTextToSize(act.description || '', contentWidth - 60);
      doc.text(descLines, margin + 60, y);
      y += descLines.length * 13 + 12;
    }
  }

  // -------------------------------------------------------------
  // 3. Typesetting Pages
  // -------------------------------------------------------------
  const totalToExport = endPage - startPage + 1;
  let exportedCount = 0;

  for (let p = startPage; p <= endPage; p++) {
    exportedCount++;
    const progress = 25 + Math.floor((exportedCount / totalToExport) * 70);
    if (exportedCount % 10 === 0 || exportedCount === totalToExport) {
      onProgress?.(progress, `Typesetting Page ${p} of ${endPage}...`);
    }

    doc.addPage();

    // Dark paper texture background
    doc.setFillColor(6, 9, 14);
    doc.rect(0, 0, pageWidth, pageHeight, 'F');

    const pageData: PageData = getPage(p);

    // Running Header
    doc.setTextColor(107, 124, 150);
    doc.setFont('courier', 'normal');
    doc.setFontSize(8.5);
    doc.text(`THE CAESURA ENGINE // ${pageData.actTitle.toUpperCase()}`, margin, 42);
    doc.text(`TICK 5.39E-44 S`, pageWidth - margin, 42, { align: 'right' });

    doc.setDrawColor(28, 39, 56);
    doc.setLineWidth(0.5);
    doc.line(margin, 48, pageWidth - margin, 48);

    let curY = 80;

    // Chapter & Page Title
    doc.setTextColor(0, 243, 255);
    doc.setFont('courier', 'bold');
    doc.setFontSize(10);
    doc.text(`PAGE ${p} // ${pageData.contentArchetype.toUpperCase()}`, margin, curY);
    curY += 16;

    doc.setTextColor(240, 246, 252);
    doc.setFont('times', 'bold');
    doc.setFontSize(16);
    const titleLines = doc.splitTextToSize(pageData.title, contentWidth);
    doc.text(titleLines, margin, curY);
    curY += titleLines.length * 18 + 12;

    // Monospace metadata box if log or clinical
    if (pageData.speaker || pageData.timestamp) {
      doc.setFillColor(12, 18, 28);
      doc.setDrawColor(0, 243, 255);
      doc.setLineWidth(0.5);
      doc.rect(margin, curY, contentWidth, 22, 'FD');

      doc.setTextColor(0, 243, 255);
      doc.setFont('courier', 'normal');
      doc.setFontSize(8.5);
      const metaStr = `OPERATOR: ${pageData.speaker || 'WARD, M.'}  |  TIMESTAMP: ${pageData.timestamp || 'PERSEUS-TICK'}`;
      doc.text(metaStr, margin + 8, curY + 14);
      curY += 34;
    }

    // Body Paragraphs
    doc.setFont('times', 'normal');
    doc.setFontSize(11);
    doc.setTextColor(216, 226, 236);

    for (let i = 0; i < pageData.text.length; i++) {
      const paragraph = pageData.text[i];

      // Format as courier if ledger or log
      if (pageData.contentArchetype === 'ledger' || pageData.contentArchetype === 'log') {
        doc.setFont('courier', 'normal');
        doc.setFontSize(9.5);
      } else {
        doc.setFont('times', 'normal');
        doc.setFontSize(11);
      }

      const lines = doc.splitTextToSize(paragraph, contentWidth);

      // Check if page overflow
      if (curY + lines.length * 15 > pageHeight - 65) {
        doc.addPage();
        doc.setFillColor(6, 9, 14);
        doc.rect(0, 0, pageWidth, pageHeight, 'F');
        curY = 60;
      }

      doc.text(lines, margin, curY);
      curY += lines.length * 15 + 10;
    }

    // Running Footer
    doc.setDrawColor(28, 39, 56);
    doc.setLineWidth(0.5);
    doc.line(margin, pageHeight - 45, pageWidth - margin, pageHeight - 45);

    doc.setTextColor(107, 124, 150);
    doc.setFont('courier', 'normal');
    doc.setFontSize(8.5);
    doc.text(`COORD HASH: 0x${(p * 0x1f3c).toString(16).toUpperCase()}`, margin, pageHeight - 32);
    doc.text(`— ${p} —`, pageWidth / 2, pageHeight - 32, { align: 'center' });
    doc.text(`THORNE-VANCE COMBINE`, pageWidth - margin, pageHeight - 32, { align: 'right' });
  }

  onProgress?.(100, 'Tome Compilation Finished!');
  return doc;
}

/**
 * Browser download trigger
 */
export async function downloadBookPDF(
  options: PdfExportOptions = {},
  fileName = 'The_Caesura_Engine_Tome.pdf'
): Promise<void> {
  const doc = await buildBookPDF(options);
  doc.save(fileName);
}
